/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useMemo } from 'react';

import {
    Button,
    ContentContainerBodyShift,
    Heading,
    Icon,
    Text,
    useBreakpoint,
    useFlags,
} from 'sarsaparilla';

import { usePassDetailsQuery } from 'ui-interagency-pass/src/api/queries';
import type { PassDetails } from 'ui-interagency-pass/src/api/interagency-pass-types';
import { useWizardContext } from 'ui-interagency-pass/src/components/PassWizard/context/WizardContext';
import { InteragencyPassCard } from 'ui-interagency-pass/src/components';

import './FindYourPass.scss';

const PASS_CARD_PLACEHOLDER_COUNT = 9;

function FindYourPass() {
    const { isLgAndUp } = useBreakpoint();
    const { releaseDigitalIapFeatures, overrideAtbPassYear } = useFlags();
    const { openWizard } = useWizardContext();
    const wizardEnabled = releaseDigitalIapFeatures?.helpMeChooseWizard ?? false;

    // Fetch pass details using React Query
    const { data: passDetails, isLoading, error } = usePassDetailsQuery();

    // Sort pass details in the specific order requested by stakeholders
    const sortedPassDetails = useMemo(() => {
        if (!passDetails) return [];

        const orderMap: Record<string, number> = {
            annual: 0,
            resident: 1,
            nonresident: 2,
            access: 3,
            'military-lifetime': 4,
            'military-annual': 5,
            'fourth-grade': 6,
            'senior-lifetime': 7,
            'senior-annual': 8,
        };

        return [...passDetails].sort((a, b) => {
            const orderA = orderMap[a.type] ?? 999; // Default to end if type not found
            const orderB = orderMap[b.type] ?? 999;
            return orderA - orderB;
        });
    }, [passDetails]);

    // Show loading state while fetching data
    if (isLoading) {
        return (
            <ContentContainerBodyShift className="mt-0 mb-6">
                <section id="find-your-pass" aria-label="Find Your Pass">
                    <Heading
                        headingLevel={3}
                        appearance={isLgAndUp ? 'h4' : 'h5'}
                        className="mt-5 mb-2"
                    >
                        Find Your Pass
                    </Heading>
                    <div className="passes-container">
                        {Array.from({ length: PASS_CARD_PLACEHOLDER_COUNT }).map(
                            (_, index) => (
                                <InteragencyPassCard
                                    key={index}
                                    pass={null as never}
                                    isLoading={true}
                                    imageSize="lg"
                                />
                            )
                        )}
                    </div>
                </section>
            </ContentContainerBodyShift>
        );
    }

    // Show error state if API call fails
    if (error) {
        return (
            <ContentContainerBodyShift className="mt-0 mb-6">
                <section id="find-your-pass" aria-label="Find Your Pass">
                    <Heading
                        headingLevel={3}
                        appearance={isLgAndUp ? 'h4' : 'h5'}
                        className="mt-5 mb-2"
                    >
                        Find Your Pass
                    </Heading>
                    <div className="text-center py-4">
                        <Text>
                            Unable to load pass information. Please try again later.
                        </Text>
                    </div>
                </section>
            </ContentContainerBodyShift>
        );
    }

    // Don't render anything if no data
    if (!sortedPassDetails || sortedPassDetails.length === 0) {
        return null;
    }

    return (
        <ContentContainerBodyShift className="mt-0 mb-6">
            <section id="find-your-pass" aria-label="Find Your Pass">
                <Heading
                    headingLevel={3}
                    appearance={isLgAndUp ? 'h4' : 'h5'}
                    className="mt-5 mb-2"
                >
                    Find Your Pass
                </Heading>
                <div className="passes-container">
                    {sortedPassDetails.map((pass: PassDetails) => (
                        <InteragencyPassCard
                            key={pass.type}
                            pass={pass}
                            addToCartEnabled={
                                releaseDigitalIapFeatures?.addToCartButton &&
                                (!releaseDigitalIapFeatures?.annualPassOnly ||
                                    pass.type === 'annual')
                            }
                            overrideAtbPassYear={overrideAtbPassYear}
                            gaTrackingId="790913691406"
                            gaTrackingValue={pass.type}
                            imageSize="lg"
                        />
                    ))}
                    {wizardEnabled && (
                        <div className="find-your-pass-card wizard-cta-tile">
                            <div
                                className="wizard-cta-tile__icon-wrap"
                                aria-hidden="true"
                            >
                                <div className="wizard-cta-tile__icon-circle">
                                    <Icon
                                        iconName="card"
                                        className="wizard-cta-tile__icon-card"
                                    />
                                    <Icon
                                        iconName="help"
                                        className="wizard-cta-tile__icon-help"
                                    />
                                </div>
                            </div>
                            <Heading
                                headingLevel={4}
                                appearance="h6"
                                className="wizard-cta-tile__title"
                            >
                                Help Me Choose a Pass
                            </Heading>
                            <Text className="wizard-cta-tile__description" tag="p">
                                Still unsure which America the Beautiful Pass type is
                                right for you? Click below to use an interactive tool to
                                help you choose!
                            </Text>
                            <Button
                                onClick={openWizard}
                                className="wizard-cta-tile__button"
                                gaTrackingId="640758009943"
                                gaTrackingValue="Help Me Choose a Pass"
                            >
                                Help Me Choose a Pass
                            </Button>
                        </div>
                    )}
                </div>
            </section>
        </ContentContainerBodyShift>
    );
}

export default FindYourPass;
